/**
 * Configure-modal store — owns the cross-surface payload between the two
 * enrollment entry points (job pipeline + talent search) and the Configure
 * modal (spec §5).
 *
 * Why a store: the job pipeline bulk-action bar, the talent-search action
 * bar, and the Configure modal are mounted in three different React trees,
 * so prop drilling the cohort + selected sequence + waterfall strategy
 * across them is impractical. A tiny Zustand store keeps the payload
 * deterministic and resettable.
 *
 * Lifecycle:
 *   - Job-side entry: `set({ jobId, jobCandidateIds, sequenceId: null })`
 *     then open the modal.
 *   - Talent-search entry: `set({ profileIds, jobId: null, jobCandidateIds: [] })`
 *     then open the modal. The modal's "Add to a position" pre-step runs
 *     `add_from_search` and rewrites `jobId` + `jobCandidateIds` from the result.
 *   - On Continue: the modal posts `useCreatePreview`, navigates to the
 *     preview route, and calls `reset()` so a stale cohort never leaks
 *     into the next enrollment attempt.
 *
 * Spec §5 (Configure modal), §13.1 (`Enrollment.Configure` compound API).
 */

import { create } from "zustand";

import { WATERFALL_DEFAULT } from "../lib/waterfall-meta";
import type { WaterfallStrategy } from "../lib/types";

export interface ConfigureState {
  /** Job UUID the cohort lives under. `null` until an entry point selects one. */
  jobId: string | null;
  /** `job_candidates.id[]` to enroll. Empty until an entry point pushes the cohort. */
  jobCandidateIds: string[];
  /**
   * Talent-search profile UUIDs that still need adding to a position before
   * they can be enrolled. Only the talent-search entry point sets this — when
   * present (and `jobCandidateIds` is empty) the Configure modal opens on its
   * "Add to a position" pre-step, runs `add_from_search`, then continues into
   * the enrollment wizard with the resulting `jobCandidateIds`. Empty for the
   * job-pipeline / sequence-tab entry points, which already arrive with a cohort.
   */
  profileIds: string[];
  /** Last sequence the user picked in the modal; persisted across modal open/close. */
  sequenceId: string | null;
  /** Last waterfall strategy the user picked; defaults to `WATERFALL_DEFAULT`. */
  waterfallStrategy: WaterfallStrategy;
  /** Merge-update for any subset of the persistent fields. */
  set: (
    partial: Partial<{
      jobId: string | null;
      jobCandidateIds: string[];
      profileIds: string[];
      sequenceId: string | null;
      waterfallStrategy: WaterfallStrategy;
    }>,
  ) => void;
  /** Wipe back to defaults — call after Continue or after Cancel. */
  reset: () => void;
}

const INITIAL_STATE = {
  jobId: null as string | null,
  jobCandidateIds: [] as string[],
  profileIds: [] as string[],
  sequenceId: null as string | null,
  waterfallStrategy: WATERFALL_DEFAULT,
};

export const useConfigureStore = create<ConfigureState>((set) => ({
  ...INITIAL_STATE,
  set: (partial) => set(partial),
  reset: () => set(INITIAL_STATE),
}));
